import React, { useState, useEffect, useRef } from "react";
import io from "socket.io-client";
import axios from "axios";
import "../styles/AdminChatDashboard.css";

let socket = null;

const AdminChatDashboard = ({ adminUser }) => {
  const safeGetId = (data) => {
    if (!data) return null;
    if (typeof data === "string") return data;
    if (data._id) return String(data._id);
    return null;
  };

  if (!socket) {
    socket = io("https://drp-backend-1y8r.onrender.com", {
      reconnection: true,
      reconnectionDelay: 1000,
      reconnectionDelayMax: 5000,
      reconnectionAttempts: 10,
      transports: ["websocket", "polling"],
      timeout: 10000,
    });

    socket.on("connect", () => {
      console.log("Admin Socket connected:", socket.id);
    });

    socket.on("disconnect", () => {
      console.log("Admin Socket disconnected");
    });
  }

  const [pendingChats, setPendingChats] = useState([]);
  const [allRooms, setAllRooms] = useState([]);
  const [stats, setStats] = useState({});
  const [activeTab, setActiveTab] = useState("pending");
  const [loading, setLoading] = useState(true);
  const [activeRoom, setActiveRoom] = useState(null);
  const [activeMessages, setActiveMessages] = useState([]);
  const [replyText, setReplyText] = useState("");
  const [isTyping, setIsTyping] = useState(false);
  const messagesEndRef = useRef(null);
  const typingTimeoutRef = useRef(null);

  const getDisplayName = (chat) => {
    if (!chat) return "Guest";
    if (chat.sender?.name) return chat.sender.name;
    if (chat.guestName) return chat.guestName;
    if (chat.guestEmail) return chat.guestEmail.split("@")[0];
    return "Guest";
  };

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [activeMessages, isTyping]);

  useEffect(() => {
    fetchDashboardData();
    const handleAdminNotification = () => fetchDashboardData();
    socket.on("adminNotification", handleAdminNotification);
    return () => socket.off("adminNotification", handleAdminNotification);
  }, []);

  useEffect(() => {
    if (!activeRoom) return;

    socket.emit("joinRoom", { room: activeRoom.room });

    const handleReceiveMessage = (newMessage) => {
      if (!newMessage.message || !newMessage.message.trim()) return;
      if (newMessage.room === activeRoom.room) {
        setActiveMessages((prev) => {
          if (prev.some((m) => m._id === newMessage._id)) return prev;
          return [...prev, newMessage];
        });
        fetchDashboardData();
      }
    };

    const handleUserTyping = ({ room, isTyping: typing }) => {
      if (room === activeRoom.room) setIsTyping(typing);
    };

    socket.on("receiveMessage", handleReceiveMessage);
    socket.on("userTyping", handleUserTyping);

    return () => {
      socket.off("receiveMessage", handleReceiveMessage);
      socket.off("userTyping", handleUserTyping);
    };
  }, [activeRoom]);

  const fetchDashboardData = async () => {
    try {
      const [pendingRes, allRoomsRes, statsRes] = await Promise.all([
        axios.get("/api/v1/chat/admin/pending-chats"),
        axios.get("/api/v1/chat/admin/all-rooms"),
        axios.get("/api/v1/chat/admin/stats"),
      ]);
      setPendingChats(pendingRes.data.chats || []);
      setAllRooms(allRoomsRes.data.rooms || []);
      setStats(statsRes.data.stats || {});
    } catch (error) {
      console.error("Dashboard fetch error:", error);
    } finally {
      setLoading(false);
    }
  };

  const fetchRoomMessages = async (roomID) => {
    try {
      const res = await axios.get(`/api/v1/chat/${roomID}`);
      console.log("Fetched messages:", res.data);
      setActiveMessages(res.data || []);
    } catch (err) {
      console.error("Fetch messages error:", err);
    }
  };

  const handleOpenChat = async (chat) => {
    setActiveRoom(chat);
    await fetchRoomMessages(chat.room);
    try {
      await axios.put(`/api/v1/chat/admin/mark-read/${chat.room}`);
      fetchDashboardData();
    } catch (err) {
      console.error("Mark read error:", err);
    }
  };

  const handleTyping = (value) => {
    setReplyText(value);
    if (!activeRoom) return;
    if (typingTimeoutRef.current) clearTimeout(typingTimeoutRef.current);
    socket.emit("typing", { room: activeRoom.room, isTyping: true });
    typingTimeoutRef.current = setTimeout(() => {
      socket.emit("typing", { room: activeRoom.room, isTyping: false });
    }, 1000);
  };

  const handleSendReply = async (customMessage = null) => {
    const text = customMessage || replyText;
    if (!text.trim() || !activeRoom) return;

    if (!adminUser || !adminUser._id) {
      console.error("Admin user missing");
      return;
    }

    const uniqueId = `admin_${Date.now()}_${Math.random()
      .toString(36)
      .substr(2, 9)}`;

    const payload = {
      _id: uniqueId,
      room: activeRoom.room,
      message: text.trim(),
      sender: {
        _id: adminUser._id,
        name: "SAAR DRP Admin Head",
        role: "admin",
      },
      isAdminMessage: true,
      createdAt: new Date().toISOString(),
    };

    try {
      setReplyText("");
      setActiveMessages((prev) => [...prev, payload]);
      socket.emit("typing", { room: activeRoom.room, isTyping: false });
      socket.emit("sendMessage", payload);

      await axios.post("/api/v1/chat/admin/reply", {
        room: activeRoom.room,
        message: payload.message,
        adminId: adminUser._id,
        email: activeRoom.sender?.email || activeRoom.guestEmail,
        isAdminMessage: true,
        sender: {
          _id: adminUser._id,
          name: "SAAR DRP Admin Head",
          role: "admin",
        },
      });
      fetchDashboardData();
    } catch (err) {
      console.error("Send reply error:", err);
      setActiveMessages((prev) => prev.filter((m) => m._id !== uniqueId));
    }
  };

  const handleResolveChat = async () => {
    if (!activeRoom || !window.confirm("Mark as resolved?")) return;
    try {
      const payload = {
        _id: `resolved_${Date.now()}`,
        room: activeRoom.room,
        message: "✅ This issue has been marked as resolved.",
        sender: {
          _id: adminUser?._id,
          name: "SAAR DRP Admin Head",
          role: "admin",
        },
        isAdminMessage: true,
        isResolvedSignal: true,
        createdAt: new Date().toISOString(),
      };
      socket.emit("sendMessage", payload);
      await axios.post("/api/v1/chat/admin/reply", {
        room: payload.room,
        message: payload.message,
        adminId: adminUser?._id,
        email: activeRoom.sender?.email || activeRoom.guestEmail,
        isAdminMessage: true,
        sender: payload.sender,
      });
      await axios.put(`/api/v1/chat/admin/resolve/${activeRoom.room}`);
      setActiveRoom(null);
      fetchDashboardData();
    } catch (err) {
      console.error("Resolve error:", err);
    }
  };

  if (loading) return <div className="admin-loader">Loading Dashboard...</div>;

  return (
    <div className="admin-chat-dashboard">
      <div className="stats-grid">
        <div className="stat-box">
          <h4>{stats.totalMessages || 0}</h4>
          <p>Messages</p>
        </div>
        <div className="stat-box pending">
          <h4>{stats.pendingChats || 0}</h4>
          <p>Pending</p>
        </div>
        <div className="stat-box">
          <h4>{stats.totalRooms || 0}</h4>
          <p>Rooms</p>
        </div>
      </div>

      <div className="dashboard-main-layout">
        <div className="chat-sidebar">
          <div className="tabs-header">
            <button
              className={activeTab === "pending" ? "active" : ""}
              onClick={() => setActiveTab("pending")}
            >
              Pending ({pendingChats.length})
            </button>
            <button
              className={activeTab === "all" ? "active" : ""}
              onClick={() => setActiveTab("all")}
            >
              All Rooms ({allRooms.length})
            </button>
          </div>
          <div className="chat-list">
            {(activeTab === "pending" ? pendingChats : allRooms).map((chat) => (
              <div
                key={chat.room}
                className={`chat-item ${
                  activeRoom?.room === chat.room ? "active" : ""
                }`}
                onClick={() => handleOpenChat(chat)}
              >
                <div className="avatar">
                  {getDisplayName(chat).charAt(0).toUpperCase()}
                </div>
                <div className="info">
                  <h5>{getDisplayName(chat)}</h5>
                  <p>{chat.lastMessage || "No messages"}</p>
                </div>
                {chat.unreadCount > 0 && (
                  <span className="unread-badge">{chat.unreadCount}</span>
                )}
              </div>
            ))}
            {(activeTab === "pending" ? pendingChats : allRooms).length ===
              0 && (
              <div className="empty-chat-list">
                <p>No chats</p>
              </div>
            )}
          </div>
        </div>

        <div className="conversation-viewport">
          {activeRoom ? (
            <>
              <div className="convo-header">
                <div>
                  <strong>{getDisplayName(activeRoom)}</strong>
                  <small>
                    {activeRoom.sender?.email || activeRoom.guestEmail}
                  </small>
                </div>
                <button className="resolve-btn" onClick={handleResolveChat}>
                  Mark Resolved
                </button>
              </div>

              <div className="convo-messages">
                {activeMessages.map((msg, i) => {
                  console.log("Message:", msg);

                  const msgSenderId = safeGetId(msg.sender);
                  const roomUserId = safeGetId(activeRoom.sender);

                  const isAdminByFlag = msg.isAdminMessage === true;
                  const isAdminByRole = msg.sender?.role === "admin";
                  const isAdminByName =
                    msg.sender?.name === "SAAR DRP Admin Head" ||
                    (msg.sender?.name &&
                      msg.sender.name.toLowerCase().includes("admin"));
                  const isAdminById =
                    msg._id &&
                    (msg._id.toString().startsWith("admin_") ||
                      msg._id.toString().startsWith("resolved_"));

                  const isUserById =
                    msgSenderId &&
                    roomUserId &&
                    String(msgSenderId) === String(roomUserId);
                  const isUserByEmail =
                    activeRoom.guestEmail &&
                    msg.sender?.email === activeRoom.guestEmail;

                  const isFromUser =
                    (isUserById || isUserByEmail) &&
                    !isAdminByFlag &&
                    !isAdminByRole;

                  const isMsgFromAdmin =
                    !isFromUser &&
                    (isAdminByFlag ||
                      isAdminByRole ||
                      isAdminByName ||
                      isAdminById);

                  const displayName = isMsgFromAdmin
                    ? "SAAR DRP Admin Head"
                    : getDisplayName(activeRoom);

                  console.log(
                    "Is Admin?",
                    isMsgFromAdmin,
                    "Display Name:",
                    displayName
                  );

                  return (
                    <div
                      key={msg._id || i}
                      className={`msg-row ${isMsgFromAdmin ? "admin" : "user"}`}
                    >
                      <div className="bubble">
                        <small className="bubble-display-name">
                          {displayName}
                        </small>
                        {msg.message}
                        <span className="time">
                          {new Date(msg.createdAt).toLocaleTimeString([], {
                            hour: "2-digit",
                            minute: "2-digit",
                          })}
                        </span>
                      </div>
                    </div>
                  );
                })}

                {isTyping && (
                  <div className="msg-row user">
                    <div className="bubble typing-bubble">
                      <div className="typing-dots">
                        <span></span>
                        <span></span>
                        <span></span>
                      </div>
                    </div>
                  </div>
                )}
                <div ref={messagesEndRef} />
              </div>

              <div className="convo-input-area">
                <div className="quick-replies">
                  <button
                    onClick={() =>
                      handleSendReply(
                        "I'm looking into this for you right now."
                      )
                    }
                  >
                    ⏱️ Checking
                  </button>
                  <button
                    onClick={() =>
                      handleSendReply("Could you please share a screenshot?")
                    }
                  >
                    📸 Screenshot
                  </button>
                  <button
                    onClick={() =>
                      handleSendReply("Could you please provide your Order ID?")
                    }
                  >
                    🆔 Order ID
                  </button>
                  <button
                    onClick={() =>
                      handleSendReply("Please try clearing your browser cache.")
                    }
                  >
                    🧹 Clear Cache
                  </button>
                  <button
                    onClick={() =>
                      handleSendReply("Our team is working on this.")
                    }
                  >
                    ⚙️ In Progress
                  </button>
                  <button
                    onClick={() => handleSendReply("Is there anything else?")}
                  >
                    ❓ Anything Else?
                  </button>
                  <button
                    onClick={() => handleSendReply("Verifying your details...")}
                  >
                    🔍 Verifying
                  </button>
                </div>
                <div className="convo-input">
                  <textarea
                    value={replyText}
                    onChange={(e) => handleTyping(e.target.value)}
                    placeholder="Type a message..."
                    onKeyDown={(e) =>
                      e.key === "Enter" &&
                      !e.shiftKey &&
                      (e.preventDefault(), handleSendReply())
                    }
                  />
                  <button
                    onClick={() => handleSendReply()}
                    disabled={!replyText.trim()}
                  >
                    Send
                  </button>
                </div>
              </div>
            </>
          ) : (
            <div className="empty-state">
              <h3>👋 Welcome to Support Dashboard</h3>
              <p>Select a conversation to start</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default AdminChatDashboard;
